// Calendar days typed by staff and stored by the API. Staff type DD/MM/YYYY
// (docs/ui-standard.md §4); the API stores YYYY-MM-DD; people read
// "20 Oct 2026".

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const pad = (n) => String(n).padStart(2, "0");

const real = (year, month, day) => {
  const d = new Date(Date.UTC(year, month - 1, day));
  return year >= 1900 && d.getUTCFullYear() === year && d.getUTCMonth() === month - 1 && d.getUTCDate() === day;
};

// Today on this computer, as YYYY-MM-DD.
export function todayIso(now = new Date()) {
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

// "2026-10-20" (or "2026-10-20T00:00:00.000Z", or "2026-10-20 00:00:00") to
// "2026-10-20"; anything else to "".
export function isoDay(value) {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T].*)?$/.exec(String(value ?? "").trim());
  if (!m) return "";
  return real(Number(m[1]), Number(m[2]), Number(m[3])) ? `${m[1]}-${m[2]}-${m[3]}` : "";
}

// What staff typed, "20/10/2026" or "20-10-2026" or "20.10.2026", to
// "2026-10-20"; "" when it is not a real day.
export function isoFromTyped(text) {
  const m = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(String(text ?? "").trim());
  if (!m) return "";
  const [day, month, year] = [Number(m[1]), Number(m[2]), Number(m[3])];
  return real(year, month, day) ? `${year}-${pad(month)}-${pad(day)}` : "";
}

// "2026-10-20" to "20/10/2026", for the field.
export function typedFromIso(iso) {
  const day = isoDay(iso);
  if (!day) return "";
  const [y, m, d] = day.split("-");
  return `${d}/${m}/${y}`;
}

// "2026-10-20" to "20 Oct 2026", for reading.
export function dayWords(iso) {
  const day = isoDay(iso);
  if (!day) return "";
  const [y, m, d] = day.split("-").map(Number);
  return `${d} ${MONTHS[m - 1]} ${y}`;
}

// Whole days from `iso` to today (positive when it is in the past).
export function daysSince(iso, now = new Date()) {
  const day = isoDay(iso);
  if (!day) return null;
  const [y, m, d] = day.split("-").map(Number);
  const then = Date.UTC(y, m - 1, d);
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((today - then) / 86400000);
}

// What is wrong with a typed day, in words, or "" when it is fine.
// Empty is fine unless `required`; a day after today is not, unless `future`.
export function typedDayError(text, { required = false, future = false, now = new Date() } = {}) {
  const value = String(text ?? "").trim();
  if (!value) return required ? "Write the date, like 20/10/2026, or press Today." : "";
  const iso = isoFromTyped(value);
  if (!iso) return "Write a real date as day, month and year, like 20/10/2026.";
  if (!future && daysSince(iso, now) < 0) return "This date is after today. Write the day it happened.";
  return "";
}
